import React from 'react';
import Seo from '../components/Seo';
import { Link } from 'react-router-dom';
import { Flame, ArrowRight } from 'lucide-react';
import { BRAND } from '../mock/mock';
import Reveal from '../components/Reveal';

const UPDATED = 'July 2025';

const Section = ({ n, title, children }) => (
  <Reveal className="mb-9">
    <h2 className="font-display text-2xl text-fire-cream mb-3">
      <span className="text-gradient-gold mr-2">{n}.</span>{title}
    </h2>
    <div className="text-fire-cream/70 font-body leading-[1.85] space-y-3">{children}</div>
  </Reveal>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-2.5">
    <Flame className="w-4 h-4 text-fire-gold shrink-0 mt-1" strokeWidth={1.6} />
    <span>{children}</span>
  </li>
);

const Privacy = () => {
  return (
    <div className="bg-fire-ink">
      <Seo title="Privacy Policy" path="/privacy" description="How After the Fire™ collects, uses and protects your personal information." />
      <section className="pt-32 pb-12 ember-texture text-center">
        <div className="max-w-3xl mx-auto px-6">
          <Flame className="w-8 h-8 text-fire-gold mx-auto mb-5 animate-flicker" strokeWidth={1.4} />
          <span className="font-marcellus tracking-[0.25em] text-fire-gold text-xs">YOUR PRIVACY MATTERS</span>
          <h1 className="font-display text-4xl md:text-6xl text-fire-cream mt-4">Privacy Policy</h1>
          <p className="text-fire-cream/55 font-body text-sm mt-4">Last updated: {UPDATED}</p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-[820px] mx-auto px-5 lg:px-8">
          <Reveal className="card-dark rounded-2xl p-6 md:p-8 mb-10">
            <p className="text-fire-cream/70 font-body leading-relaxed">
              At After the Fire™, we understand that trust is sacred — especially for women sharing tender parts of their
              story. This Privacy Policy explains what information we collect, how we use it, and the choices you have. We are
              committed to handling your information with the same care and respect we bring to every Fireside conversation.
            </p>
          </Reveal>

          <Section n="1" title="Information We Collect">
            <p>We only collect what we need to serve you well. This may include:</p>
            <ul className="space-y-2.5 mt-2">
              <Bullet><span className="text-fire-cream/90">Information you provide</span> — such as your name, email, organization and any details you share when you book a call, request an event, contact us, place an order or join our mailing list.</Bullet>
              <Bullet><span className="text-fire-cream/90">What you share in a Fireside Clarity Call™</span> — treated as private and confidential, and never sold or shared for marketing.</Bullet>
              <Bullet><span className="text-fire-cream/90">Order &amp; payment details</span> — processed securely through our payment provider when our store is live; we do not store full card numbers.</Bullet>
              <Bullet><span className="text-fire-cream/90">Usage information</span> — basic technical data such as browser type and pages visited, collected through cookies and analytics to improve the site.</Bullet>
            </ul>
          </Section>

          <Section n="2" title="How We Use Your Information">
            <p>We use the information you provide to:</p>
            <ul className="space-y-2.5 mt-2">
              <Bullet>Schedule and deliver Fireside Clarity Calls™, events and bookings.</Bullet>
              <Bullet>Process and fulfill merchandise, book and digital orders.</Bullet>
              <Bullet>Respond to your questions and booking inquiries.</Bullet>
              <Bullet>Send encouragement, event dates and updates — only if you’ve opted in.</Bullet>
              <Bullet>Maintain, protect and improve our website and services.</Bullet>
            </ul>
          </Section>

          <Section n="3" title="Confidentiality of Your Story">
            <p>
              What you share in a private conversation with Dr. Gee stays private. We do not sell, rent or trade the personal
              details of your circumstances. Please note that Fireside Clarity Calls™ are peer/lived-experience support and are
              not confidential in the legal sense of licensed therapy or medical care.
            </p>
          </Section>

          <Section n="4" title="How We Share Information">
            <p>
              We do not sell your personal information. We share it only with trusted service providers who help us operate —
              such as email, scheduling, analytics and payment processors — and only to the extent needed to perform those
              services. We may also disclose information if required by law or to protect the safety and rights of others.
            </p>
          </Section>

          <Section n="5" title="Cookies &amp; Analytics">
            <p>
              Our site may use cookies and similar technologies to remember your preferences (like items in your bag) and to
              understand how the site is used. You can control cookies through your browser settings; disabling them may affect
              some features, such as the shopping bag.
            </p>
          </Section>

          <Section n="6" title="Your Choices &amp; Rights">
            <ul className="space-y-2.5">
              <Bullet>Unsubscribe from emails at any time using the link in any message.</Bullet>
              <Bullet>Request access to, correction of, or deletion of your personal information.</Bullet>
              <Bullet>Ask us any question about how your information is handled.</Bullet>
            </ul>
            <p className="mt-2">To exercise any of these, email us at{' '}
              <a href={`mailto:${BRAND.email}`} className="text-fire-goldlight underline underline-offset-2 hover:text-fire-gold">{BRAND.email}</a>.
            </p>
          </Section>

          <Section n="7" title="Data Security">
            <p>
              We use reasonable administrative, technical and physical safeguards to protect your information. No method of
              transmission over the internet is 100% secure, but we work to keep your information safe and limit access to it.
            </p>
          </Section>

          <Section n="8" title="Children’s Privacy">
            <p>
              After the Fire™ is intended for adult women. We do not knowingly collect personal information from children under
              13. If you believe a child has provided us information, please contact us so we can remove it.
            </p>
          </Section>

          <Section n="9" title="Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. Updates take effect when posted here with a revised date. We
              encourage you to review this page periodically.
            </p>
          </Section>

          <Section n="10" title="Contact Us">
            <p>
              Questions or requests about your privacy? We’re here:{' '}
              <a href={`mailto:${BRAND.email}`} className="text-fire-goldlight underline underline-offset-2 hover:text-fire-gold">{BRAND.email}</a>.
            </p>
          </Section>

          <Reveal className="mt-12 rounded-2xl p-8 text-center bg-gradient-to-br from-fire-maroon to-fire-maroondark border border-fire-gold/30">
            <p className="font-cormorant italic text-fire-cream text-xl">
              We honor your story — and your privacy.
            </p>
            <div className="flex flex-wrap gap-4 justify-center mt-6">
              <Link to="/terms" className="btn-outline-gold rounded-full px-6 py-3 font-semibold text-sm">Read our Terms &amp; Conditions</Link>
              <Link to="/contact" className="btn-gold rounded-full px-6 py-3 font-semibold inline-flex items-center gap-2 text-sm">
                Contact Dr. Gee <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
